import { useState, useEffect, useMemo } from 'react';
import {
  FiUsers,
  FiUserCheck,
  FiClock,
  FiCheckCircle,
  FiCircle,
  FiSlash,
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import api from '../../utils/api';
import StatCard from '../../components/StatCard';
import Badge from '../../components/Badge';

const BAR_COLORS = [
  '#6B8063',
  '#D4A017',
  '#3498DB',
  '#9B59B6',
  '#E67E22',
  '#C0392B',
  '#1ABC9C',
];

const STEP_KEYS = [
  'training_request',
  'chair_approval',
  'application_form',
  'acceptance_letter',
  'sent_to_hr',
  'sent_to_logistics',
  'sent_to_infection_control',
  'sent_to_university',
  'sent_to_chairperson_rtp',
  'general_orientation',
  'payment_received',
  'end_evaluation',
];

const STEP_LABELS = [
  'Request',
  'Chair Appr.',
  'App. Form',
  'Accept. Letter',
  'HR',
  'Logistics',
  'Inf. Control',
  'University',
  'Chair/RTP',
  'Orientation',
  'Payment',
  'Evaluation',
];

export default function TraineeDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [summary, setSummary] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [loading, setLoading] = useState(true);

  // Tracker filters
  const [filterType, setFilterType] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterCompletion, setFilterCompletion] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryRes, trackerRes] = await Promise.all([
          api.get('/trainees/stats/summary'),
          api.get('/trainees/stats/tracker'),
        ]);
        setSummary(summaryRes);
        setTracker(trackerRes);
      } catch (err) {
        console.error('Failed to load trainee data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Derive filter options from tracker data
  const trainingTypes = useMemo(() => {
    if (!tracker?.trainees) return [];
    return [...new Set(tracker.trainees.map((t) => t.training_type).filter(Boolean))].sort();
  }, [tracker]);

  const departments = useMemo(() => {
    if (!tracker?.trainees) return [];
    return [...new Set(tracker.trainees.map((t) => t.department).filter(Boolean))].sort();
  }, [tracker]);

  // Filtered trainees
  const filteredTrainees = useMemo(() => {
    if (!tracker?.trainees) return [];
    return tracker.trainees.filter((t) => {
      if (filterType && t.training_type !== filterType) return false;
      if (filterDept && t.department !== filterDept) return false;
      if (filterCompletion === 'complete' && t.completion_pct !== 100) return false;
      if (filterCompletion === 'in_progress' && t.completion_pct >= 100) return false;
      return true;
    });
  }, [tracker, filterType, filterDept, filterCompletion]);

  if (loading) {
    return (
      <div className="card text-center p-4">
        <div className="spinner" style={{ margin: '40px auto' }} />
      </div>
    );
  }

  // Chart data
  const byTypeData = (summary?.by_type || []).map((item) => ({
    name: item.training_type,
    count: item.count,
  }));

  const byDeptData = (summary?.by_department || []).map((item) => ({
    name: item.department,
    count: item.count,
  }));

  const tabButtonStyle = (tab) => ({
    padding: '8px 20px',
    fontSize: '0.85rem',
    fontWeight: 600,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: activeTab === tab ? '#6B8063' : '#f0ece4',
    color: activeTab === tab ? '#ffffff' : '#2B362E',
    transition: 'all 0.2s ease',
  });

  return (
    <div>
      {/* Tab Toggle */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        <button style={tabButtonStyle('dashboard')} onClick={() => setActiveTab('dashboard')}>
          Dashboard
        </button>
        <button style={tabButtonStyle('tracker')} onClick={() => setActiveTab('tracker')}>
          Process Tracker
        </button>
      </div>

      {activeTab === 'dashboard' ? (
        <DashboardView summary={summary} byTypeData={byTypeData} byDeptData={byDeptData} />
      ) : (
        <TrackerView
          trainees={filteredTrainees}
          trainingTypes={trainingTypes}
          departments={departments}
          filterType={filterType}
          setFilterType={setFilterType}
          filterDept={filterDept}
          setFilterDept={setFilterDept}
          filterCompletion={filterCompletion}
          setFilterCompletion={setFilterCompletion}
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────
   Tab 1: Dashboard View
   ──────────────────────────────────────────── */

function DashboardView({ summary, byTypeData, byDeptData }) {
  const status = summary?.by_status || {};

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-4" style={{ marginBottom: '24px' }}>
        <StatCard title="Total Trainees" value={summary?.total || 0} icon={FiUsers} />
        <StatCard
          title="Active Trainees"
          value={status.active || 0}
          icon={FiUserCheck}
          color="#6B8063"
        />
        <StatCard
          title="Completed Training"
          value={status.completed || 0}
          icon={FiCheckCircle}
          color="#3498DB"
        />
        <StatCard
          title="Upcoming Trainees"
          value={status.upcoming || 0}
          icon={FiClock}
          color="#D4A017"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-2" style={{ marginBottom: '24px' }}>
        {/* By Type */}
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Trainees by Type</h4>
          {byTypeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(200, byTypeData.length * 44)}>
              <BarChart data={byTypeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  width={140}
                />
                <Tooltip />
                <Bar dataKey="count" name="Trainees" radius={[0, 4, 4, 0]}>
                  {byTypeData.map((_, i) => (
                    <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center" style={{ padding: '40px 0' }}>
              No training type data available.
            </p>
          )}
        </div>

        {/* By Department */}
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Trainees by Department</h4>
          {byDeptData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(200, byDeptData.length * 44)}>
              <BarChart data={byDeptData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ece4" />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  width={140}
                />
                <Tooltip />
                <Bar dataKey="count" name="Trainees" fill="#6B8063" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center" style={{ padding: '40px 0' }}>
              No department data available.
            </p>
          )}
        </div>
      </div>

      {/* Detail Tables Row */}
      <div className="grid grid-2" style={{ marginBottom: '24px' }}>
        {/* By Type Table */}
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Detailed Breakdown by Type</h4>
          {byTypeData.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Training Type</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {byTypeData.map((row, i) => (
                  <tr key={i} style={tableRowStyle(i)}>
                    <td style={tableCellStyle}>{row.name}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 600 }}>
                      {row.count}
                    </td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#f0ece4' }}>
                  <td style={{ ...tableCellStyle, fontWeight: 700 }}>Total</td>
                  <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 700 }}>
                    {byTypeData.reduce((sum, r) => sum + r.count, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p className="text-muted text-center" style={{ padding: '40px 0' }}>
              No data available.
            </p>
          )}
        </div>

        {/* By Department Table */}
        <div className="card">
          <h4 style={{ marginBottom: '16px' }}>Detailed Breakdown by Department</h4>
          {byDeptData.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Department</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {byDeptData.map((row, i) => (
                  <tr key={i} style={tableRowStyle(i)}>
                    <td style={tableCellStyle}>{row.name}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 600 }}>
                      {row.count}
                    </td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#f0ece4' }}>
                  <td style={{ ...tableCellStyle, fontWeight: 700 }}>Total</td>
                  <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 700 }}>
                    {byDeptData.reduce((sum, r) => sum + r.count, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p className="text-muted text-center" style={{ padding: '40px 0' }}>
              No data available.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────
   Tab 2: Process Tracker View
   ──────────────────────────────────────────── */

function TrackerView({
  trainees,
  trainingTypes,
  departments,
  filterType,
  setFilterType,
  filterDept,
  setFilterDept,
  filterCompletion,
  setFilterCompletion,
}) {
  const selectStyle = {
    padding: '6px 12px',
    fontSize: '0.8rem',
    border: '1px solid #d9d4cb',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#2B362E',
    outline: 'none',
    minWidth: '160px',
  };

  return (
    <div>
      {/* Filter Bar */}
      <div
        className="card"
        style={{
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          padding: '12px 20px',
        }}
      >
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#2B362E' }}>Filters:</span>

        <select style={selectStyle} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {trainingTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <select style={selectStyle} value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
          <option value="">All Departments</option>
          {departments.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        <select
          style={selectStyle}
          value={filterCompletion}
          onChange={(e) => setFilterCompletion(e.target.value)}
        >
          <option value="all">All Progress</option>
          <option value="complete">Complete (100%)</option>
          <option value="in_progress">In Progress</option>
        </select>

        <span style={{ fontSize: '0.75rem', color: '#6b6b6b', marginLeft: 'auto' }}>
          Showing {trainees.length} trainee{trainees.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Tracker Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {trainees.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
              <thead>
                <tr>
                  <th style={trackerHeaderStyle}>Trainee Name</th>
                  <th style={trackerHeaderStyle}>Type</th>
                  <th style={trackerHeaderStyle}>Department</th>
                  {STEP_LABELS.map((label) => (
                    <th key={label} style={trackerStepHeaderStyle} title={label}>
                      {label}
                    </th>
                  ))}
                  <th style={{ ...trackerHeaderStyle, minWidth: '110px' }}>Completion</th>
                </tr>
              </thead>
              <tbody>
                {trainees.map((trainee, idx) => (
                  <tr
                    key={trainee.id || idx}
                    style={{
                      backgroundColor: idx % 2 === 0 ? '#ffffff' : '#faf8f4',
                      borderBottom: '1px solid #f0ece4',
                    }}
                  >
                    <td style={trackerCellStyle}>
                      <span style={{ fontWeight: 500, fontSize: '0.8rem', color: '#2B362E' }}>
                        {trainee.trainee_name}
                      </span>
                    </td>
                    <td style={trackerCellStyle}>
                      <Badge status="active" text={trainee.training_type} />
                    </td>
                    <td style={{ ...trackerCellStyle, fontSize: '0.78rem', color: '#555' }}>
                      {trainee.department}
                    </td>
                    {STEP_KEYS.map((key) => (
                      <td key={key} style={trackerStepCellStyle}>
                        {trainee.steps?.[key] === 'skipped' ? (
                          <FiSlash size={16} color="#B2AB9C" />
                        ) : trainee.steps?.[key] ? (
                          <FiCheckCircle size={16} color="#6B8063" />
                        ) : (
                          <FiCircle size={16} color="#e0dcd4" />
                        )}
                      </td>
                    ))}
                    <td style={trackerCellStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div
                          style={{
                            width: '80px',
                            height: '6px',
                            backgroundColor: '#f0ece4',
                            borderRadius: '999px',
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        >
                          <div
                            style={{
                              width: `${trainee.completion_pct || 0}%`,
                              height: '100%',
                              backgroundColor:
                                trainee.completion_pct === 100 ? '#6B8063' : '#D4A017',
                              borderRadius: '999px',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            color: '#2B362E',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {trainee.completion_pct || 0}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <FiUsers size={36} color="#B2AB9C" />
            <p style={{ color: '#6b6b6b', marginTop: '12px', fontSize: '0.9rem' }}>
              No trainees match your current filters.
            </p>
            <p style={{ color: '#B2AB9C', fontSize: '0.8rem' }}>
              Try adjusting the filters above.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────
   Shared table styles
   ──────────────────────────────────────────── */

const tableHeaderStyle = {
  padding: '10px 14px',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#6b6b6b',
  textAlign: 'left',
  borderBottom: '2px solid #f0ece4',
};

const tableCellStyle = {
  padding: '10px 14px',
  fontSize: '0.82rem',
  color: '#2B362E',
};

const tableRowStyle = (i) => ({
  backgroundColor: i % 2 === 0 ? '#ffffff' : '#faf8f4',
  borderBottom: '1px solid #f0ece4',
});

const trackerHeaderStyle = {
  padding: '10px 12px',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#ffffff',
  backgroundColor: '#2B362E',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const trackerStepHeaderStyle = {
  padding: '8px 4px',
  fontSize: '0.65rem',
  fontWeight: 600,
  color: '#ffffff',
  backgroundColor: '#2B362E',
  textAlign: 'center',
  width: '40px',
  minWidth: '40px',
  maxWidth: '50px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const trackerCellStyle = {
  padding: '8px 12px',
  fontSize: '0.8rem',
  color: '#2B362E',
  whiteSpace: 'nowrap',
};

const trackerStepCellStyle = {
  padding: '6px 4px',
  textAlign: 'center',
  width: '40px',
  minWidth: '40px',
};
